import React, { useRef, useState, useCallback, forwardRef, useImperativeHandle } from "react";
import { LuUpload, LuPlay, LuPause } from "react-icons/lu";
import i18n from "i18next";
import { isFileTooLarge, fileTooLargeMessage } from "../../../common/upload_limits.es6";

const ALLOWED_EXTENSIONS = ["mp3", "wav", "m4a"];

const UploadSoundTab = forwardRef(({ onFileUpload, uploadFunction, onAddSound, onError }, ref) => {
  const fileInputRef = useRef(null);
  const audioRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [fileName, setFileName] = useState("");
  const [fileSize, setFileSize] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [startTime, setStartTime] = useState("0");
  const [volume, setVolume] = useState(86);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [progress, setProgress] = useState(0);
  /** "" | "uploading" | "done" — the inline replacement for the old popup. */
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  const validateFile = (file) => {
    const extension = file.name.split(".").pop()?.toLowerCase() || "";
    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      return (
        i18n.t("AddSoundUpload.ErrorInvalidType") || "Only MP3, WAV, or M4A files can be uploaded."
      );
    }
    if (isFileTooLarge(file)) {
      return fileTooLargeMessage();
    }
    return "";
  };

  const processFile = (file) => {
    if (!file) return;
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError("");
    // A new pick clears whatever the last upload said.
    setStatus("");
    setFileName(file.name);
    setFileSize(`${(file.size / 1024).toFixed(0)} KB`);
    setUploadedFile(file);
    const url = URL.createObjectURL(file);
    setAudioUrl(url);
    onFileUpload?.(file);
  };

  const handleFileSelect = (event) => {
    processFile(event.target.files?.[0]);
    // allow re-selecting the same file after a validation error
    event.target.value = "";
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    processFile(event.dataTransfer.files?.[0]);
  };

  const handleRemove = () => {
    setError("");
    setStatus("");
    setFileName("");
    setFileSize("");
    setUploadedFile(null);
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioUrl(null);
    setIsPlaying(false);
    setCurrentTime(0);
    setProgress(0);
    setDuration(0);
    onFileUpload?.(null);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handlePlayPause = useCallback(() => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  }, [isPlaying]);

  const handleTimeUpdate = useCallback(() => {
    if (!audioRef.current) return;
    const cur = audioRef.current.currentTime;
    const dur = audioRef.current.duration || 0;
    setCurrentTime(cur);
    setProgress(dur > 0 ? (cur / dur) * 100 : 0);
  }, []);

  const handleLoadedMetadata = useCallback(() => {
    if (!audioRef.current) return;
    setDuration(audioRef.current.duration || 0);
  }, []);

  const handleAudioEnded = useCallback(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    setProgress(0);
  }, []);

  const handleProgressBarClick = useCallback((e) => {
    if (!audioRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const percentage = (e.clientX - rect.left) / rect.width;
    const newTime = percentage * audioRef.current.duration;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
    setProgress(percentage * 100);
  }, []);

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  useImperativeHandle(
    ref,
    () => ({
      handleSave: () => {
        if (!uploadedFile) {
          onError?.();
          return;
        }
        if (!uploadFunction) {
          console.error("[UploadSoundTab] uploadFunction prop is missing");
          onError?.();
          return;
        }
        // Yükleme geri bildirimi (spinner → "Sahneye eklendi" + Tamam)
        // AddSoundModal'ın kendi overlay'i tarafından yönetiliyor; burada
        // ayrıca serverModal toast'ı göstermiyoruz (çift/çakışan popup olmasın).
        // uploadVishResourceAsync hata durumunda success callback'i
        // url=undefined ile çağırır; onError overlay spinner'ını temizler
        // (aksi halde overlay ekranı kaplar ve çıkış kalmaz).
        setStatus("uploading");
        uploadFunction(uploadedFile, null, (url, diagnostics) => {
          if (!url) {
            console.error("[UploadSoundTab] upload failed", diagnostics);
            setStatus("");
            onError?.();
            return;
          }
          setStatus("done");
          onAddSound?.({
            file: url,
            name: fileName || uploadedFile.name,
            startTime: Number(startTime) || 0,
            endTime: (Number(startTime) || 0) + duration,
            volumeLevel: volume / 100,
          });
        });
      },
    }),
    [uploadedFile, fileName, startTime, duration, volume, uploadFunction, onAddSound, onError]
  );

  return (
    <div className="add-sound-modal__upload">
      <input
        ref={fileInputRef}
        type="file"
        className="add-sound-modal__upload-input"
        style={{ display: "none" }}
        accept=".mp3,.wav,.m4a,audio/mpeg,audio/wav,audio/x-wav,audio/x-m4a,audio/mp4"
        onChange={handleFileSelect}
      />
      {!fileName ? (
        <div
          className={`add-sound-modal__upload-dropzone${
            isDragging ? " add-sound-modal__upload-dropzone--active" : ""
          }`}
          onClick={() => fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <div className="add-sound-modal__upload-icon">
            <LuUpload />
          </div>
          <div className="add-sound-modal__upload-title">
            {i18n.t("AddSoundUpload.DropTitle") || "Click to upload or drag and drop"}
          </div>
          <div className="add-sound-modal__upload-subtitle">
            {i18n.t("AddSoundUpload.DropSubtitle") || "MP3, WAV, or M4A (max 100MB)"}
          </div>
        </div>
      ) : (
        <div className="add-sound-modal__upload-file">
          <div className="add-sound-modal__upload-file-icon">🎵</div>
          <div className="add-sound-modal__upload-file-info">
            <div className="add-sound-modal__upload-file-name">{fileName}</div>
            <div className="add-sound-modal__upload-file-size">{fileSize}</div>
          </div>
          <button
            type="button"
            className="add-sound-modal__upload-file-remove"
            onClick={handleRemove}
          >
            {i18n.t("AddSoundUpload.Remove") || "Remove"}
          </button>
        </div>
      )}

      {error && <div className="add-sound-modal__upload-error">{error}</div>}

      {status && (
        <div
          className={`add-sound-modal__upload-status add-sound-modal__upload-status--${status}`}
          role="status"
          aria-live="polite"
        >
          {status === "uploading"
            ? i18n.t("SoundLoading") || "Ses yükleniyor..."
            : i18n.t("SoundLoaded") || "Ses yüklendi"}
        </div>
      )}

      {fileName && audioUrl && (
        <>
          <audio
            ref={audioRef}
            src={audioUrl}
            preload="metadata"
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
            onEnded={handleAudioEnded}
            style={{ display: "none" }}
          />
          <div className="add-sound-modal__ai-voice-player" style={{ marginTop: "16px" }}>
            <button
              type="button"
              className="add-sound-modal__ai-voice-play"
              onClick={handlePlayPause}
            >
              {isPlaying ? <LuPause /> : <LuPlay />}
            </button>
            <div
              className="add-sound-modal__ai-voice-bar"
              onClick={handleProgressBarClick}
              style={{ cursor: "pointer" }}
            >
              <span
                className="add-sound-modal__ai-voice-bar-fill"
                style={{ width: `${progress}%` }}
              />
            </div>
            <span className="add-sound-modal__ai-voice-time">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          </div>
        </>
      )}

      {fileName && (
        <div className="add-sound-modal__upload-settings">
          <div className="add-sound-modal__upload-settings-title">
            {i18n.t("AddSoundUpload.SoundSettings") || "Sound Settings"}
          </div>
          <div className="add-sound-modal__upload-settings-grid">
            <div className="add-sound-modal__upload-settings-field">
              <label>{i18n.t("AddSoundUpload.StartTime") || "Start Time"}</label>
              <input
                type="number"
                value={startTime}
                onChange={(event) => setStartTime(event.target.value)}
              />
            </div>
            <div className="add-sound-modal__upload-settings-field">
              <div className="add-sound-modal__upload-settings-row">
                <label>{i18n.t("AddSoundUpload.Volume") || "Volume"}</label>
                <span>{volume}</span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={volume}
                onChange={(event) => setVolume(Number(event.target.value))}
                className="add-sound-modal__upload-settings-range"
                style={{
                  background: `linear-gradient(90deg, #6d5dfc ${volume}%, #e2e8f0 ${volume}%)`,
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default UploadSoundTab;
